import React from "react";
import {
    Code2,
    ExternalLink,
    FileText,
    GitBranch,
    Layers,
    Mail,
    Smartphone,
    Zap,
    type LucideIcon,
} from "lucide-react";
import classes from "./HelloWorld.module.css";

type FeatureCard = {
    icon: LucideIcon;
    title: string;
    description: string;
};

const featureCards: FeatureCard[] = [
    {
        icon: Smartphone,
        title: "Mobile First",
        description: "Specialized in React Native and cross-platform development",
    },
    {
        icon: Code2,
        title: "8+ Years",
        description: "Professional experience in software development",
    },
    {
        icon: Layers,
        title: "Enterprise",
        description: "Worked with Fortune 500 companies",
    },
    {
        icon: Zap,
        title: "Innovation",
        description: "Building next-gen no-code solutions",
    },
];

const timeline = [
    {
        active: true,
        time: "Present",
        title: "No-Code Platform Developer",
        company: "Independent Project",
        description:
            "Developing a revolutionary low-cost no-code platform that enables users to generate fully functional mobile applications in less than an hour. The platform features advanced Figma integration, allowing designers to seamlessly convert their designs into production-ready mobile apps. This innovative solution democratizes app development and significantly reduces time-to-market.",
    },
    {
        time: "2022",
        title: "Senior Mobile Engineer",
        company: "Postpay",
        description:
            "Built secure payment experiences for a financial product, including Apple Pay, recurring payments, biometric login, GraphQL-powered payment flows, and PCI-DSS-focused implementation. Improved engineering quality with reusable hooks, smooth adaptive UI, CI/CD automation, build alerts, and release procedures that reduced reinstall/build overhead by roughly half.",
    },
    {
        time: "2019 - 2022",
        title: "React Native Developer",
        company: "MoMo",
        description:
            "Contributed to Vietnam's leading super-app wallet serving more than 20 million users. Owned complex bank-linking, payment, eKYC, SDK bridge, dynamic configuration, analytics, and search flows across more than 40 banks, helping reduce app size, support real-time business changes, strengthen fraud prevention, and improve user engagement at fintech scale.",
    },
    {
        time: "2018 - 2019",
        title: "Software Developer",
        company: "KPMG",
        description:
            "Delivered enterprise mobile solutions for a Big Four environment, including BMW customer service tooling, OCR odometer recognition, secure anti-hacking keyboard flows, and analytics instrumentation. Worked across high-trust business applications where usability, security, and operational visibility were core requirements.",
    },
    {
        time: "2015 - 2018",
        title: "Software Development Foundation",
        company: "TAFE SA & Personal Projects",
        description:
            "Graduated with a Bachelor of Software Development while leading and contributing to cross-platform student apps, iOS development, and project management work. Built Catalee, a React Native boilerplate with multilingual support, theming, remote config, code push, reusable components, deep linking, documentation, and CI/CD automation for faster project delivery.",
    },
];

const projectTags = ["React Native", "Figma API", "No-Code", "AI-Powered", "Design Systems"];

const projectHighlights = [
    "Generate production-ready apps in less than 60 minutes",
    "Direct Figma integration for design-to-code conversion",
    "Affordable pricing for startups and small businesses",
];

const skillGroups = [
    {
        title: "Mobile Development",
        items: ["React Native", "iOS & Android", "Mobile UI/UX", "App Store Deployment"],
    },
    {
        title: "Frontend & Web",
        items: ["React.js", "JavaScript/TypeScript", "Responsive Design", "State Management"],
    },
    {
        title: "Tools & Platforms",
        items: ["Figma API Integration", "Git & CI/CD", "Agile Development", "No-Code Platforms"],
    },
];

const scrollToSection = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({behavior: "smooth"});
};

const HelloWorld = () => {
    const currentYear = new Date().getFullYear();

    return (
        <main className={classes.helloworld}>
            <nav className={classes.nav} aria-label="Primary navigation">
                <div className={classes.navInner}>
                    <a className={classes.brand} href="#top" onClick={(event) => scrollToSection(event, "top")}>Hua Cat Luong</a>
                    <div className={classes.navLinks}>
                        <a href="#about" onClick={(event) => scrollToSection(event, "about")}>About</a>
                        <a href="#experience" onClick={(event) => scrollToSection(event, "experience")}>Experience</a>
                        <a href="#projects" onClick={(event) => scrollToSection(event, "projects")}>Projects</a>
                        <a href="#contact" onClick={(event) => scrollToSection(event, "contact")}>Contact</a>
                    </div>
                </div>
            </nav>

            <section id="top" className={`${classes.section} ${classes.hero}`}>
                <div className={classes.container}>
                    <div className={classes.heroContent}>
                        <h1>Software Developer specialized in React Native</h1>
                        <p>
                            Senior React Native engineer with 8 years of experience designing, building,
                            publishing, and scaling high-profile mobile applications for fintech, enterprise,
                            and consumer platforms. Currently building a no-code platform that turns ideas and
                            Figma designs into production-ready mobile apps faster.
                        </p>
                        <div className={classes.actions}>
                            <a className={classes.primaryButton} href="#contact" onClick={(event) => scrollToSection(event, "contact")}>Get in Touch</a>
                            <a className={classes.secondaryButton} href="#projects" onClick={(event) => scrollToSection(event, "projects")}>View Projects</a>
                        </div>
                    </div>
                </div>
            </section>

            <section id="about" className={`${classes.section} ${classes.accentSection}`}>
                <div className={`${classes.container} ${classes.aboutGrid}`}>
                    <div>
                        <h2>About Me</h2>
                        <p>
                            I am a dedicated mobile engineer with deep React Native experience across the full
                            product lifecycle, from architecture and implementation to release, analytics,
                            security, and long-term maintenance. My work has supported enterprise teams,
                            fintech platforms, and mobile products used by millions of people.
                        </p>
                        <p>
                            I enjoy turning complex requirements into reliable, elegant mobile experiences.
                            Today, I am applying that experience to make app development more accessible
                            through a practical no-code platform for production-ready applications.
                        </p>
                    </div>

                    <div className={classes.featureGrid}>
                        {featureCards.map((feature) => (
                            <article className={classes.card} key={feature.title}>
                                <feature.icon className={classes.icon} aria-hidden="true"/>
                                <h3>{feature.title}</h3>
                                <p>{feature.description}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section id="experience" className={classes.section}>
                <div className={classes.container}>
                    <h2>Experience</h2>
                    <div className={classes.timeline}>
                        {timeline.map((entry) => (
                            <article
                                className={`${classes.timelineEntry} ${entry.active ? classes.activeEntry : ""}`}
                                key={`${entry.company}-${entry.title}`}
                            >
                                <span className={classes.timelineDot}/>
                                <span className={classes.timeLabel}>{entry.time}</span>
                                <h3>{entry.title}</h3>
                                <p className={classes.company}>{entry.company}</p>
                                <p>{entry.description}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section id="projects" className={`${classes.section} ${classes.accentSection}`}>
                <div className={classes.container}>
                    <article className={classes.projectCard}>
                        <div className={classes.projectGrid}>
                            <div>
                                <span className={classes.badge}>Current Project</span>
                                <h2>Figma-to-Mobile No-Code Platform</h2>
                                <p>
                                    A groundbreaking platform that transforms the app development process. Users can
                                    create fully functional mobile applications in under an hour using an intuitive
                                    no-code interface. The platform's flagship feature allows seamless Figma design
                                    imports, automatically converting design files into native mobile components.
                                </p>
                                <div className={classes.tags}>
                                    {projectTags.map((tag) => <span key={tag}>{tag}</span>)}
                                </div>
                                <ul className={classes.highlightList}>
                                    {projectHighlights.map((highlight) => (
                                        <li key={highlight}>
                                            <span/>
                                            {highlight}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className={classes.projectPreview}>
                                <Layers className={classes.icon} aria-hidden="true"/>
                                <span>Platform Preview</span>
                            </div>
                        </div>
                    </article>
                </div>
            </section>

            <section className={classes.section}>
                <div className={classes.container}>
                    <h2>Skills & Technologies</h2>
                    <div className={classes.skillsGrid}>
                        {skillGroups.map((group) => (
                            <article className={classes.skillCard} key={group.title}>
                                <h3>{group.title}</h3>
                                <ul>
                                    {group.items.map((item) => <li key={item}>{item}</li>)}
                                </ul>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section id="contact" className={`${classes.section} ${classes.accentSection} ${classes.contact}`}>
                <div className={classes.container}>
                    <h2>Let's Work Together</h2>
                    <p>
                        I'm always interested in hearing about new projects and opportunities. Whether you have a
                        question or just want to say hi, feel free to reach out!
                    </p>
                    <div className={classes.contactActions}>
                        <span className={classes.primaryButton}>
                            <Mail className={classes.icon} aria-hidden="true"/>
                            hualuong23596@gmail.com
                        </span>
                        <a className={classes.primaryButton} href="/resume" target="_blank" rel="noopener noreferrer">
                            <FileText className={classes.icon} aria-hidden="true"/>
                            View Resume
                        </a>
                        <a className={classes.secondaryButton} href="https://www.linkedin.com/in/huacatluong/" target="_blank" rel="noopener noreferrer">
                            <ExternalLink className={classes.icon} aria-hidden="true"/>
                            LinkedIn
                        </a>
                        <a className={classes.secondaryButton} href="https://github.com/Tichuottt/" target="_blank" rel="noopener noreferrer">
                            <GitBranch className={classes.icon} aria-hidden="true"/>
                            GitHub
                        </a>
                    </div>
                </div>
            </section>

            <footer className={classes.footer}>
                <div className={classes.container}>
                    <p>© {currentYear} Hua Cat Luong. All rights reserved.</p>
                </div>
            </footer>
        </main>
    );
};

export default HelloWorld;
